import * as React from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  ButtonGroup,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Typography,
  InputAdornment,
  TextField,
  ListItem,
  ListItemText,
  List,
  Box,
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import { LoadingComponent } from "../loading/LoadingComponent";
import { currencyFormat, bidPlacedFormat } from "../../helpers/text-format/TextFormat";
import {
  useAuctionBidHistory,
  Bid,
  useBiddersOnAuction,
  sellDirectToBidAuction,
  useAuctionListing,
  auctionLotNotCollected,
  sellDirectWithNewBidAuction,
} from "../../services/AuctionService";
import * as Styles from "./styles/SellDirectToBidderStyles";
import { useHistory } from "react-router-dom";
import { AuctionStatus } from "../../interfaces/auctions/IAuction";
import { IBidder } from "../../interfaces/bidders/IBidder";
import SearchIcon from "@material-ui/icons/Search";
import { SearchBidders } from "../../services/BidderSearchService";
import useDebounce from "../../hooks/useDebounce";

interface SellDirectToBidderComponent {
  auctionId: string;
  showSellToNewBidder: boolean;
  setShowSellToNewBidder: (value: boolean) => void;
}

export const SellDirectToBidderComponent: React.FC<SellDirectToBidderComponent> = ({ auctionId, showSellToNewBidder, setShowSellToNewBidder }) => {
  const history = useHistory();

  const [open, setOpen] = React.useState<boolean>(false);
  const [error, setError] = React.useState<boolean>(false);
  const [bid, setBid] = React.useState<Bid>();
  const [amount, setAmount] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);

  const [bidderEmail, setBidderEmail] = React.useState<string>('');
  const [bidderId, setBidderId] = React.useState<string | null>(null);
  const [bidderList, setBidderList] = React.useState<IBidder[]>([]);
  const [emailFocus, setEmailFocus] = React.useState<boolean>(false);
  const [submitted, setSubmitted] = React.useState<boolean>(false);

  const debouncedSearch = useDebounce(bidderEmail, 150); // wait 150 ms between searchin on change

  const { enqueueSnackbar } = useSnackbar();
  const classes = Styles.SellDirectToBidderStyles();
  const [bidHistory, bidHistoryLoading] = useAuctionBidHistory(auctionId, () => {
    enqueueSnackbar("There was a problem with your auctions request please try again.", { variant: "error" });
  });
  const [bidders, biddersLoading] = useBiddersOnAuction(auctionId, () => {
    enqueueSnackbar("There was a problem with your bidder request please try again.", { variant: "error" });
  });

  const [auctionListing, , auctionListingLoading] = useAuctionListing(auctionId!, () => {
    enqueueSnackbar("There was a problem with your bidder request please try again.", { variant: "error" });
  });

  const handleSellDirectFromBid = async (auctionId: string, bidId: string) => {
    try {
      await sellDirectToBidAuction(auctionId, bidId);
      history.push(`/AuctionManagement/Auctions`);
    } catch {
      enqueueSnackbar("There was a problem with selling direct to the bidder, please try again.", { variant: "error" });
    }
  };

  const handleSellDirectWithAmount = async (auctionId: string, bidderId: string, amount: number) => {
    try {
      await sellDirectWithNewBidAuction(auctionId, bidderId, amount);
      history.push(`/AuctionManagement/Auctions`);
    } catch {
      enqueueSnackbar("There was a problem with selling direct to the bidder, please try again.", { variant: "error" });
    }
  };

  const handleLotNotCollected = async () => {
    try {
      await auctionLotNotCollected(auctionId);
    } catch {
      enqueueSnackbar("There was a problem with selling direct to the bidder, please try again.", { variant: "error" });
    }
  };

  const handleSubmit = async () => {
    try {
      if (bid) {
        setLoading(true);
        if (auctionListing?.status === AuctionStatus.AuctionEndedAndWon) {
          await handleLotNotCollected();
        }
        if (amount === bid.amount) {
          await handleSellDirectFromBid(auctionId, bid.bidId);
        } else await handleSellDirectWithAmount(auctionId, bid.bidderId, amount);
      } else {
        enqueueSnackbar("There was a problem with selling direct to the bidder, please try again.", { variant: "error" });
      }
    } catch {
      enqueueSnackbar("There was a problem with selling direct to the bidder, please try again.", { variant: "error" });
    } finally {
      setLoading(false);
      closeDialog();
    }
  };

  const openDialog = (newBid: Bid) => {
    setBid(newBid);
    setAmount(newBid.amount);
    setOpen(true);
  };

  const closeDialog = () => {
    setAmount(0);
    setOpen(false);
  };

  const handleAmountChange = e => {
    if (e.target.value % 1 !== 0) setError(true);
    else {
      setError(false);
    }
    setAmount(e.target.value === "" ? 0 : Math.floor(e.target.value));
  };

  const handleSelectBidder = (bidder: IBidder) => {
    setBidderEmail(bidder.email);
    setBidderId(bidder.id);
  };

  const handleBidderInputChange = (email: string) => {
    setBidderEmail(email);

    const match = bidderList.filter(o => o.email.toLowerCase() === email.toLowerCase())[0];

    if (match) {
      setBidderEmail(match.email);
      setBidderId(match.id);
    }
  }

  const closeSellToNewBidderModal = () => {
    setShowSellToNewBidder(false);
    setBidderEmail('');
    setBidderId(null);
    setAmount(0);
  };

  React.useEffect(() => {
    if (debouncedSearch) {
      searchBidders();
    } else {
      setBidderList([]);
      setBidderId(null);
    }
  }, [debouncedSearch]);

  React.useEffect(() => {
    // This ensures we auto select the bidder and set the bidder id if an exact email is entered.
    const bidder = bidderList.find(b => b.email === bidderEmail);
    if (bidder) {
      setBidderId(bidder.id);
    } else {
      setBidderId(null);  // Clear bidderId if no match is found so that when changing the bidder email, we dont have a previously selected id..
    }
  }, [debouncedSearch, bidders]);


  const searchBidders = () => {
    if (bidderEmail?.length < 5) {
      setBidderList([]);
      setBidderId(null);
      return;
    }

    //setIsLoading(true);
    SearchBidders({
      search: bidderEmail,
      pageNumber: 0,
      pageSize: 999999,
      filter: "8"
    })
      .then((result) => {
        if (result.parsedBody?.results) {
          setBidderList(result.parsedBody.results as IBidder[]);
        }
      })
      .catch((err) => {
        enqueueSnackbar("An error occured when searching for subscribed bidders, please try again.", { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const sellDirect = async () => {
    setSubmitted(true);

    if (amount <= 0)
      return;

    try {
      setLoading(true);
      if (auctionListing?.status === AuctionStatus.AuctionEndedAndWon) {
        await handleLotNotCollected();
      }
      
      if (bidderId) {
        handleSellDirectWithAmount(auctionId, bidderId, amount);
      } else {
        enqueueSnackbar("There was a problem with selling direct to the bidder, please try again.", { variant: "error" });
      }
    }
    catch (err) {
      enqueueSnackbar("There was a problem with selling direct to the bidder, please try again.", { variant: "error" });
    }
    finally {
      setLoading(false);
      setSubmitted(false);
      closeSellToNewBidderModal();
    }
  };

  const AmountDialog = () => (
    <Dialog open={open} fullWidth maxWidth="xs">
      <DialogTitle>Sell Direct</DialogTitle>
      <DialogContent>
        <Box p={2}>
        {loading ? (
          <LoadingComponent label="Loading" marginTop="0px" marginBottom="0px" />
        ) : (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <Typography>Sell to Bidder: </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>{bid?.bidderName ?? ""}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>Enter Amount: </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                type="number"
                error={error}
                helperText={error ? "Must be a whole number." : null}
                value={amount === 0 ? "" : amount}
                onChange={value => handleAmountChange(value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
        )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>Cancel</Button>
        <Button disabled={loading || amount <= 0} onClick={handleSubmit} variant="contained" color="primary">
          Sell Direct
        </Button>
      </DialogActions>
    </Dialog>
  );

  const sellToNewBidderDialog = () => (
    <Dialog
      open={showSellToNewBidder}
      onClose={closeSellToNewBidderModal}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <Box pr={5} pl={5} pt={2}>
          Sell Direct to New Bidder
        </Box>
      </DialogTitle>

      <DialogContent>
      <Box p={5} pt={2} pb={3}>
        {loading && (
          <LoadingComponent label="Please wait..." marginTop="10px" marginBottom="10px" />
        )}

        {!loading && (
          <Grid container>
            <Grid container item xs={12} justify="space-between" alignItems="flex-end" spacing={2}>
              <Grid item xs={6}>
                <Typography align="left">
                  Sell to Bidder
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  required
                  fullWidth
                  name="bidderEmail"
                  type="text"
                  value={bidderEmail}
                  autoComplete="off"
                  onChange={(event) => handleBidderInputChange(event.target.value)}
                  onFocus={() => setEmailFocus(true)}
                  onBlur={() => setTimeout(() => {
                    setEmailFocus(false);
                  }, 250)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon color={bidderEmail?.length < 5 ? "disabled" : "primary"} />
                      </InputAdornment>
                    ),
                  }}
                />

                {emailFocus && !loading && bidderList.length > 0 && (
                  <Paper style={{ position: "fixed", zIndex: 10 }}>
                    <List>
                      {bidderList.map((bidder) => (
                        <ListItem button key={bidder.id} onClick={() => handleSelectBidder(bidder)}>
                          <ListItemText primary={bidder.email} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Grid>
            </Grid>

            <Grid container item xs={12} justify="space-between" alignItems="flex-end" spacing={2}>
              <Grid item xs={6}>
                <Typography align="left">
                  Enter Amount:
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  required
                  fullWidth
                  name="amount"
                  type="number"
                  value={amount}
                  onChange={(event) => {
                    try {
                      const newValue = event.target.value == "-0" ? 0 : Math.abs(parseInt(event.target.value))

                      if (!isNaN(newValue)) {
                        setAmount(newValue)
                        event.target.value = newValue.toString();
                      } else {
                        setAmount(0)
                        event.target.value = "0";
                      }
                    } catch(_) {}
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>
                  }}
                  inputProps={{
                    min: 0
                  }}
                />
              </Grid>
            </Grid>

            {amount <= 0 && submitted && (
              <Grid container item xs={12} spacing={2} style={{marginTop: '12px'}}>
                <Grid item xs={12}>
                  <Typography align="left" style={{ color: 'red' }}>
                    Please enter a sell direct amount greater than £0.
                  </Typography>
                </Grid>
              </Grid>
            )}

            <Grid container item xs={12} justify="flex-end" spacing={1} style={{ marginTop: '1rem' }}>
              <Grid item>
              <Box  pt={3}>
                <Button color="default" onClick={closeSellToNewBidderModal}>
                  Cancel
                </Button>
                </Box>
              </Grid>

              <Grid item>
                <Box  pt={3}>
                <Button variant="contained" color="primary" onClick={sellDirect} disabled={bidderId === null}>
                  Sell Direct
                </Button>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        )}
        </Box>
      </DialogContent>
    </Dialog>
  );

  const bidRow = (bid: Bid) => {
    const bidder = bidders?.find(b => b.id === bid.bidderId);
    return (
      <TableRow key={bid.bidId}>
        <TableCell>{bid.bidderName}</TableCell>
        <TableCell>
          <p className={classes.margin}>{bidder?.email}</p>
          <p className={classes.margin}>{bidder?.landlineNumber}</p>
          <p className={classes.margin}>{bidder?.mobileNumber}</p>
        </TableCell>
        <TableCell>
          <p className={classes.margin}>
            {bidder?.address.line1} {bidder?.address.line2} {bidder?.address.line3}
          </p>
          <p className={classes.margin}>
            {bidder?.address.city} {bidder?.address.county} {bidder?.address.country.name}
          </p>
          <p className={classes.margin}>{bidder?.address.postcode}</p>
        </TableCell>
        <TableCell>
          <p className={classes.margin}>{currencyFormat(bid.amount)}</p>
        </TableCell>
        <TableCell>{bidPlacedFormat(bid.bidDateTime.toString())}</TableCell>
        <TableCell>
          <ButtonGroup color="primary" fullWidth>
            <Button
              className={classes.buttonWidth}
              onClick={() => {
                openDialog(bid);
              }}
            >
              Sell To Bidder
            </Button>
          </ButtonGroup>
        </TableCell>
      </TableRow>
    );
  };

  return auctionListingLoading && bidHistoryLoading && biddersLoading ? (
    <LoadingComponent label="Loading" />
  ) : (
    <>
      {showSellToNewBidder && (
        <>
          {sellToNewBidderDialog()}
        </>
      )}

      <Grid container>
        <Grid xs={12} item>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Bidder name</TableCell>
                  <TableCell>Contact details</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>
                    <p className={classes.margin}>Amount</p>
                    <p className={classes.margin}>Proxy amount</p>{" "}
                  </TableCell>
                  <TableCell>Date/time</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bidHistory && bidHistory.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>No bids available</TableCell>
                  </TableRow>
                )}
                {bidHistory && bidHistory.map((bid: Bid) => bidRow(bid))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {AmountDialog()}
    </>
  );
};
